// Task #2408 — Friendly fallback for unknown payment_status values.
//
// `trips.payment_status` is a smallint on prod (see the PAYMENT_STATUS_*
// constants in api-server/src/routes/bookings.ts) but the web/mobile UI
// only knows about the string variants ("paid", "scheduled", "failed",
// "pending"). The booking pages used to leak the raw value into the UI,
// e.g. an orange "4" pill on the price-breakdown card, or — the bug this
// now fixes — render a fully PAID booking (payment_status=4) as a
// misleading "Pending" payout chip because the numeric code matched no
// string tone.
//
// This helper is the single source of truth for turning a raw
// payment_status into something safe to render. The verified numeric codes
// that have a matching tone are mapped (0=unpaid -> pending, 4=paid ->
// paid; see api-server lib/payment-status-codes.ts) so a paid booking shows
// "Paid"; everything else — empty strings, null, garbage, the
// never-observed speculative codes (1, 2), and refunded (3), which has no
// dedicated tone today — collapses to the "pending" tone with a localized
// "Pending" label so guests and hosts always see a meaningful value.

export type PaymentStatusTone = "paid" | "failed" | "scheduled" | "pending" | "neutral";

export interface ResolvedPaymentStatus {
  tone: PaymentStatusTone;
  label: string;
}

// Permissive translator signature so we can accept the i18next `t` directly
// (its overloads include `(key, defaultValue)`) as well as the simple test
// stub used in payment-status.test.ts.
type Translate = (key: string, defaultValue?: any) => string;

const KNOWN_TONES: Record<string, PaymentStatusTone> = {
  paid: "paid",
  failed: "failed",
  scheduled: "scheduled",
  pending: "pending",
};

const TERMINAL_UNCHARGED_STATUSES = new Set(["cancelled", "canceled", "declined", "expired", "no_show"]);

export function isTerminalUnchargedLifecycle(status: unknown): boolean {
  return typeof status === "string" && TERMINAL_UNCHARGED_STATUSES.has(status.trim().toLowerCase());
}

// Verified numeric `trips.payment_status` codes (api-server
// lib/payment-status-codes.ts): 0=unpaid, 3=refunded, 4=paid. Only the
// codes with a clean tone are listed here; 3 (refunded) and the
// never-observed speculative codes (1, 2) intentionally fall through to
// the "pending" fallback, since there is no dedicated "refunded" tone.
const NUMERIC_TONES: Record<number, PaymentStatusTone> = {
  0: "pending", // PAYMENT_STATUS_UNPAID — no charge captured yet
  4: "paid", // PAYMENT_STATUS_PAID
};

export function resolvePaymentStatusTone(value: unknown): PaymentStatusTone {
  if (typeof value === "string") {
    const key = value.trim().toLowerCase();
    if (key in KNOWN_TONES) return KNOWN_TONES[key];
    // Smallint codes occasionally arrive as numeric strings (e.g. "4");
    // treat them the same as their numeric form.
    if (/^\d+$/.test(key)) {
      const code = Number(key);
      if (code in NUMERIC_TONES) return NUMERIC_TONES[code];
    }
    return "pending";
  }
  if (typeof value === "number" && Number.isFinite(value) && value in NUMERIC_TONES) {
    return NUMERIC_TONES[value];
  }
  return "pending";
}

export function resolvePaymentStatus(
  value: unknown,
  t: Translate,
): ResolvedPaymentStatus {
  const tone = resolvePaymentStatusTone(value);
  const label = (() => {
    switch (tone) {
      case "paid":
        return t("trip.payment_status_paid", "Paid");
      case "failed":
        return t("trip.payment_status_failed", "Failed");
      case "scheduled":
        return t("trip.payment_status_scheduled", "Scheduled");
      case "pending":
      default:
        return t("trip.payment_status_pending_label", "Pending");
    }
  })();
  return { tone, label };
}

export function resolveLifecyclePaymentStatus(
  args: { tripStatus: unknown; paymentStatus: unknown },
  t: Translate,
): ResolvedPaymentStatus {
  const code = typeof args.paymentStatus === "string" && /^\d+$/.test(args.paymentStatus.trim())
    ? Number(args.paymentStatus.trim())
    : args.paymentStatus;
  if (code === 3) {
    return { tone: "neutral", label: t("trip.payment_status_refunded", "Refunded") };
  }
  if (isTerminalUnchargedLifecycle(args.tripStatus) && code !== 4) {
    return { tone: "neutral", label: t("trip.payment_status_not_charged", "Not charged") };
  }
  return resolvePaymentStatus(args.paymentStatus, t);
}

// Task #4754 — the HOST payout chip.
//
// The host's "Payout" pill on OwnerBookingDetailPage used to be derived
// from the GUEST's `trips.payment_status` (4 = renter paid), which made a
// future trip the renter had already paid for read "Paid" before the host
// had earned (or been sent) a penny. A payout only fires after the trip
// completes, so the host pill must come from the host-side
// `trips.payout_status` instead:
//
//   0 = scheduled        — payout will run after the trip
//   2 = paid             — transfer fired
//   3 = held_by_admin    — payout paused by ops
//   4 = parked_pending_connect — host must finish payout setup
//
// We additionally treat explicit host-side evidence of a completed payout
// (a completed payout link or an admin-recorded manual payout) as "paid",
// mirroring the page's existing `payoutCompleted` signal. We deliberately
// never look at the guest `payment_status` here.

export type HostPayoutTone = "paid" | "scheduled" | "held" | "action" | "neutral";

export interface ResolvedHostPayout {
  tone: HostPayoutTone;
  label: string;
}

function toPayoutCode(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }
  return null;
}

export function resolveHostPayoutStatus(
  args: { payoutStatus: unknown; payoutCompleted: boolean },
  t: Translate,
): ResolvedHostPayout {
  const code = toPayoutCode(args.payoutStatus);
  // Paid: the platform transfer fired (2) OR we have direct host-side
  // evidence of a completed payout (completed link / recorded manual).
  if (code === 2 || args.payoutCompleted) {
    return { tone: "paid", label: t("owner_booking.payout_status_paid", "Paid") };
  }
  if (code === 3) {
    return { tone: "held", label: t("owner_booking.payout_status_on_hold", "On hold") };
  }
  if (code === 4) {
    return { tone: "action", label: t("owner_booking.payout_status_action_needed", "Action needed") };
  }
  // 0 (scheduled), null, or any other code on a still-live booking: the
  // payout has not happened yet — it is scheduled for after the trip.
  return { tone: "scheduled", label: t("owner_booking.payout_status_scheduled", "Scheduled") };
}

export function resolveLifecycleHostPayoutStatus(
  args: { tripStatus: unknown; payoutStatus: unknown; payoutCompleted: boolean },
  t: Translate,
): ResolvedHostPayout {
  const resolved = resolveHostPayoutStatus(args, t);
  if (resolved.tone === "paid") return resolved;
  if (isTerminalUnchargedLifecycle(args.tripStatus) && resolved.tone === "scheduled") {
    return { tone: "neutral", label: t("owner_booking.payout_status_none", "No payout") };
  }
  return resolved;
}
